import { doc, setDoc, getDoc, getDocs, deleteDoc, updateDoc, collection, query, where, arrayUnion } from 'firebase/firestore';
import { db as firestore } from '../firebaseConfig';
import { JoinRequest } from '../types/common';
import { generateId } from './helpers';

/**
 * Create a join request for a "Sur demande" group
 */
export async function createJoinRequest(
    userId: string,
    userName: string,
    userAvatar: string | undefined,
    groupId: string,
    groupName: string
): Promise<JoinRequest> {
    const requestId = generateId('req');
    const request: JoinRequest = {
        id: requestId,
        playerId: userId,
        playerName: userName,
        playerAvatar: userAvatar,
        groupId,
        groupName,
        status: 'pending',
        createdAt: new Date().toISOString()
    };

    const requestRef = doc(firestore, 'groups', groupId, 'joinRequests', requestId);
    await setDoc(requestRef, request);

    return request;
}

/**
 * Check if user already has a pending request for this group
 */
export async function hasPendingRequest(userId: string, groupId: string): Promise<boolean> {
    const requestsRef = collection(firestore, 'groups', groupId, 'joinRequests');
    const q = query(requestsRef, where('playerId', '==', userId), where('status', '==', 'pending'));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
}

/**
 * Get user's pending request for a group (if any)
 */
export async function getUserPendingRequest(userId: string, groupId: string): Promise<JoinRequest | null> {
    const requestsRef = collection(firestore, 'groups', groupId, 'joinRequests');
    const q = query(requestsRef, where('playerId', '==', userId), where('status', '==', 'pending'));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as JoinRequest;
}

/**
 * Accept a join request (Admin action)
 */
export async function acceptJoinRequest(
    request: JoinRequest,
    _adminId: string
): Promise<void> {
    // 1. Add player to group members array
    const groupRef = doc(firestore, 'groups', request.groupId);
    await updateDoc(groupRef, {
        members: arrayUnion(request.playerId)
    });

    // 2. Create player in subcollection
    const playerRef = doc(firestore, 'groups', request.groupId, 'players', request.playerId);
    const playerSnapshot = await getDoc(playerRef);
    if (!playerSnapshot.exists()) {
        // Create minimal player entry
        await setDoc(playerRef, {
            id: request.playerId,
            name: request.playerName,
            avatar: request.playerAvatar || null,
            createdAt: new Date().toISOString(),
            joinedAt: new Date().toISOString(),
            groupJoinDate: new Date().toISOString(),
            elo: 1000,
            rating: 1500
        });
    }

    // 3. Delete the join request
    const requestRef = doc(firestore, 'groups', request.groupId, 'joinRequests', request.id);
    await deleteDoc(requestRef);

    // 4. Send notification to player (handled separately in UI)
}

/**
 * Reject a join request (Admin action)
 */
export async function rejectJoinRequest(
    request: JoinRequest,
    _adminId: string,
    _message?: string
): Promise<void> {
    // Delete the join request
    const requestRef = doc(firestore, 'groups', request.groupId, 'joinRequests', request.id);
    await deleteDoc(requestRef);

    // Notification to player is handled separately in UI
}

/**
 * Cancel own join request (User action)
 */
export async function cancelJoinRequest(request: JoinRequest): Promise<void> {
    const requestRef = doc(firestore, 'groups', request.groupId, 'joinRequests', request.id);
    await deleteDoc(requestRef);
}

/**
 * Send notification to group admins about a new join request
 */
export async function notifyAdminsOfJoinRequest(
    request: JoinRequest,
    groupAdmins: string[],
    groupOwnerId?: string
): Promise<void> {
    const recipients = [...new Set([...groupAdmins, groupOwnerId].filter(Boolean) as string[])];

    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    for (const adminId of recipients) {
        const notifId = generateId('notif');
        const notifRef = doc(firestore, 'users', adminId, 'notifications', notifId);
        await setDoc(notifRef, {
            id: notifId,
            type: 'join_request',
            playerId: adminId, // Recipient
            senderId: request.playerId,
            groupId: request.groupId,
            title: `Demande d'adhésion`,
            message: `${request.playerName} souhaite rejoindre ${request.groupName}`,
            actionData: {
                requestId: request.id,
                groupId: request.groupId,
                playerId: request.playerId,
                playerName: request.playerName,
                groupName: request.groupName,
                requestDate: dateStr,
                requestTime: timeStr
            },
            createdAt: now.toISOString(),
            timestamp: now.toISOString(),
            read: false
        });
    }
}

/**
 * Send result notification to the player who requested to join
 */
export async function notifyPlayerOfRequestResult(
    playerId: string,
    groupId: string,
    groupName: string,
    accepted: boolean,
    rejectionMessage?: string
): Promise<void> {
    const notifId = generateId('notif');
    const notifRef = doc(firestore, 'users', playerId, 'notifications', notifId);
    const now = new Date();

    // Build actionData without undefined fields
    const actionData: Record<string, any> = {
        groupId,
        groupName,
        accepted
    };
    // Only add rejectionMessage if it exists
    if (rejectionMessage) {
        actionData.rejectionMessage = rejectionMessage;
    }

    await setDoc(notifRef, {
        id: notifId,
        type: 'join_request_result',
        playerId: playerId,
        groupId: groupId,
        title: accepted ? 'Demande acceptée !' : 'Demande refusée',
        message: accepted
            ? `Votre demande pour rejoindre "${groupName}" a été acceptée. Bienvenue !`
            : `Votre demande pour rejoindre "${groupName}" a été refusée.${rejectionMessage ? ` Motif: ${rejectionMessage}` : ''}`,
        actionData,
        createdAt: now.toISOString(),
        timestamp: now.toISOString(),
        read: false
    });
}
